import { useEffect, useState } from 'react'
import { adminApi } from '../lib/api'
import { transformArray } from '../lib/transformers'
import { CheckCircle, XCircle, Trash2 } from 'lucide-react'
import type { ModerationItem } from '../types'
import { useToast } from '../components/ui/ToastProvider'

export default function ModerationPage() {
  const { showToast } = useToast()
  const [items, setItems] = useState<ModerationItem[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<string>('all')

  useEffect(() => {
    loadModerationQueue()
  }, [filter])

  const loadModerationQueue = async () => {
    setLoading(true)
    try {
      const response = await adminApi.getModerationQueue({ type: filter !== 'all' ? filter : undefined })

      // Handle different response structures
      let rawData = []

      if (!response.data) {
        rawData = []
      } else if (Array.isArray(response.data)) {
        rawData = response.data
      } else if (typeof response.data === 'object') {
        // API returns {success: true, data: {queue: [], pagination: {}}}
        rawData = response.data?.data?.queue || response.data?.queue || response.data?.data || response.data?.items || response.data?.results || []
      }

      // Transform snake_case to camelCase
      const data = transformArray<ModerationItem>(Array.isArray(rawData) ? rawData : [])
      setItems(data)
    } catch (error) {
      console.error('Failed to load moderation queue:', error)
      setItems([])
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (item: ModerationItem) => {
    try {
      if (item.type === 'comment') {
        await adminApi.approveComment(item.id)
      } else if (item.type === 'trip') {
        await adminApi.approveTrip(item.id)
      } else if (item.type === 'event') {
        await adminApi.approveEvent(item.id)
      } else if (item.type === 'photo') {
        await adminApi.approvePhoto(item.id)
      }
      loadModerationQueue()
    } catch (error) {
      console.error('Failed to approve item:', error)
    }
  }

  const handleReject = async (item: ModerationItem) => {
    const reason = prompt('Reason for rejection:')
    if (!reason) return

    try {
      if (item.type === 'comment') {
        await adminApi.rejectComment(item.id)
      } else if (item.type === 'trip') {
        await adminApi.rejectTrip(item.id, reason)
      } else if (item.type === 'event') {
        await adminApi.rejectEvent(item.id, reason)
      } else if (item.type === 'photo') {
        await adminApi.rejectPhoto(item.id)
      }
      loadModerationQueue()
    } catch (error) {
      console.error('Failed to reject item:', error)
    }
  }

  const handleDelete = async (item: ModerationItem) => {
    if (!confirm('Are you sure you want to delete this item?')) return

    try {
      if (item.type === 'comment') {
        await adminApi.deleteComment(item.id)
      } else if (item.type === 'trip') {
        await adminApi.deleteTrip(item.id)
      } else if (item.type === 'event') {
        await adminApi.deleteEvent(item.id)
      } else if (item.type === 'photo') {
        // Photos are deprecated, use Posts instead
        showToast('Photo moderation has been moved to Posts. Please use the Posts page.', {
          type: 'info',
        })
        return
      } else if (item.type === 'message') {
        // For messages, we need both chatId and messageId
        // Assuming item.content contains chatId, or we extract it from item
        const chatId = (item.content as any)?.chatId || item.id
        await adminApi.deleteChatMessage(chatId, item.id)
      }
      loadModerationQueue()
    } catch (error) {
      console.error('Failed to delete item:', error)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Moderation Queue</h1>
        <p className="text-gray-600 mt-1">Review and moderate user-generated content</p>
      </div>

      <div className="card">
        <div className="flex gap-2 mb-6">
          {['all', 'comment', 'trip', 'event', 'photo', 'message'].map((type) => (
            <button
              key={type}
              onClick={() => setFilter(type)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === type
                  ? 'text-black hover:opacity-90'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              style={filter === type ? { backgroundColor: '#97DF06' } : undefined}
            >
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No items in moderation queue
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <div key={item.id} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="badge badge-info">{item.type}</span>
                      {item.reports > 0 && (
                        <span className="badge badge-warning">{item.reports} reports</span>
                      )}
                      <span className="text-sm text-gray-500">
                        {new Date(item.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <div className="text-sm text-gray-900">
                      {JSON.stringify(item.content).slice(0, 200)}...
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleApprove(item)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                      title="Approve"
                    >
                      <CheckCircle className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleReject(item)}
                      className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"
                      title="Reject"
                    >
                      <XCircle className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
